import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { DiscoverPreview, CompatibilityPreview } from "@/components/AppPreview"; // eslint-disable-line no-unused-vars
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Heart, ShieldCheck, Sparkles, Lock, Users, Search, Compass, MessageCircle,
  UserCog, ArrowRight, Check, Menu, Star, HeartHandshake, BookHeart, Download,
  Quote, MapPin, RotateCw,
} from "lucide-react";

const NAV = [
  { label: "How It Works", href: "#how" },
  { label: "Features", href: "#features" },
  { label: "Marriage Board", href: "/marriage-board" },
  { label: "Success Stories", href: "#stories" },
  { label: "FAQ", href: "#faq" },
  { label: "Download App", href: "/download.html" },
];

// Verified Quran translations (Sahih International) on marriage & companionship.
const DAILY_VERSES = [
  { text: "And of His signs is that He created for you from yourselves mates that you may find tranquility in them; and He placed between you affection and mercy.", ref: "Qur'an 30:21 (Ar-Rum)" },
  { text: "Our Lord, grant us from among our spouses and offspring comfort to our eyes and make us an example for the righteous.", ref: "Qur'an 25:74 (Al-Furqan)" },
  { text: "It is He who created you from one soul and created from it its mate that he might dwell in security with her.", ref: "Qur'an 7:189 (Al-A'raf)" },
  { text: "They are a garment for you and you are a garment for them.", ref: "Qur'an 2:187 (Al-Baqarah)" },
  { text: "And marry the unmarried among you and the righteous among your male and female servants.", ref: "Qur'an 24:32 (An-Nur)" },
  { text: "And of all things We created two mates; perhaps you will remember.", ref: "Qur'an 51:49 (Adh-Dhariyat)" },
  { text: "O mankind, fear your Lord, who created you from one soul and created from it its mate and dispersed from both of them many men and women.", ref: "Qur'an 4:1 (An-Nisa)" },
];

function getDailyVerse() {
  const now = new Date();
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
  return DAILY_VERSES[dayOfYear % DAILY_VERSES.length];
}

// Curated, licensed promotional wedding photos (illustrative — not members).
const U = "?crop=entropy&cs=srgb&fm=jpg&q=85&ixlib=rb-4.1.0";
const MAIN_PHOTOS = [
  { src: `https://images.unsplash.com/photo-1629097388620-168b50020969${U}&w=800`, alt: "A smiling Muslim bride and groom sharing a warm moment" },
  { src: `https://images.unsplash.com/photo-1611421126481-521a1bdc1679${U}&w=800`, alt: "A Muslim couple in wedding attire" },
  { src: `https://images.unsplash.com/photo-1709122993551-0aab19063c17${U}&w=800`, alt: "A Muslim couple in traditional wedding dress" },
  { src: `https://images.unsplash.com/photo-1610048334981-206b679a178e${U}&w=800`, alt: "A bride and groom in traditional attire" },
  { src: `https://images.unsplash.com/photo-1660455559502-8f71b47443c4${U}&w=800`, alt: "A Muslim bride at her nikah ceremony" },
  { src: `https://images.unsplash.com/photo-1726694064556-c9565e8e81c9${U}&w=800`, alt: "A Muslim couple together" },
  { src: `https://images.unsplash.com/photo-1644337111604-aa1816b542a1${U}&w=800`, alt: "A couple seated at their nikah ceremony" },
  { src: `https://images.pexels.com/photos/26990489/pexels-photo-26990489.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=800`, alt: "A joyful couple celebrating their wedding" },
];
const SUPPORT_PHOTOS = [
  { src: `https://images.unsplash.com/photo-1767617774446-5bb3ce40824d${U}&w=500`, alt: "A joyful Muslim family celebrating a wedding" },
  { src: `https://images.unsplash.com/photo-1630198908899-fc1226ddbac4${U}&w=500`, alt: "A bride holding her wedding bouquet" },
  { src: `https://images.unsplash.com/photo-1673073206031-5294044a9433${U}&w=500`, alt: "A bride making dua on her wedding day" },
  { src: `https://images.unsplash.com/photo-1542042161784-26ab9e041e89${U}&w=500`, alt: "A couple exchanging wedding rings" },
  { src: `https://images.unsplash.com/photo-1673073220743-ee78b02eb029${U}&w=500`, alt: "A bride on her wedding day" },
  { src: `https://images.pexels.com/photos/5166590/pexels-photo-5166590.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=500`, alt: "A traditional wedding ceremony with family" },
  { src: `https://images.pexels.com/photos/38680060/pexels-photo-38680060.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=500`, alt: "A bride and groom exchanging rings" },
  { src: `https://images.pexels.com/photos/30608496/pexels-photo-30608496.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=500`, alt: "A wedding party celebrating outdoors" },
];
const MAIN_FALLBACK = MAIN_PHOTOS[0].src;
const SUPPORT_FALLBACK = SUPPORT_PHOTOS[0].src;

function pickHeroPhotos() {
  let lastMain = null, lastSup = [];
  try { lastMain = sessionStorage.getItem("nm_hero_main"); lastSup = JSON.parse(sessionStorage.getItem("nm_hero_sup") || "[]"); } catch { /* no-op */ }

  const mainPool = MAIN_PHOTOS.filter((p) => p.src !== lastMain);
  const main = (mainPool.length ? mainPool : MAIN_PHOTOS)[Math.floor(Math.random() * (mainPool.length ? mainPool.length : MAIN_PHOTOS.length))];

  const supPool = SUPPORT_PHOTOS.filter((p) => !lastSup.includes(p.src));
  const base = supPool.length >= 2 ? supPool : SUPPORT_PHOTOS;
  const shuffled = [...base].sort(() => Math.random() - 0.5);
  const s1 = shuffled[0];
  const s2 = shuffled.find((p) => p.src !== s1.src) || SUPPORT_PHOTOS.find((p) => p.src !== s1.src);

  try { sessionStorage.setItem("nm_hero_main", main.src); sessionStorage.setItem("nm_hero_sup", JSON.stringify([s1.src, s2.src])); } catch { /* no-op */ }
  return { main, s1, s2 };
}

function onImgError(e, fallback) {
  if (e.currentTarget.dataset.fb) return;
  e.currentTarget.dataset.fb = "1";
  e.currentTarget.src = fallback;
}

function storyMeta(s) {
  return [s.country, s.weddingDate && new Date(s.weddingDate).getFullYear()].filter(Boolean).join(" • ");
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const dailyVerse = getDailyVerse();
  const [hero] = useState(pickHeroPhotos);
  const [stories, setStories] = useState(null); // null = loading, [] = confirmed empty, [...] = data
  const [storiesError, setStoriesError] = useState(false);

  const loadStories = async () => {
    setStoriesError(false);
    setStories(null);
    try {
      const { data } = await api.get("/stories");
      setStories(data.stories || data.items || []);
    } catch {
      setStoriesError(true);
    }
  };
  useEffect(() => { loadStories(); }, []);

  useEffect(() => { if (user && user.profile_complete) navigate("/discovery"); }, [user, navigate]);

  const principles = [
    { icon: HeartHandshake, title: "Serious marriage intentions", desc: "Everyone here is seeking nikah, not casual dating." },
    { icon: Sparkles, title: "Compatibility insights", desc: "See how your values and preferences align." },
    { icon: Heart, title: "Mutual interest", desc: "Connections form only when interest is shared both ways." },
    { icon: Lock, title: "Privacy controls", desc: "You decide what's visible and when." },
    { icon: ShieldCheck, title: "Wali involvement", desc: "Include a guardian where supported, the halal way." },
  ];

  const steps = [
    { n: 1, icon: UserCog, title: "Create your profile", desc: "Share your values, intentions and what matters for marriage." },
    { n: 2, icon: Compass, title: "Discover matches", desc: "Browse curated and searchable profiles with compatibility signals." },
    { n: 3, icon: Heart, title: "Express interest", desc: "Show interest respectfully — the other person is notified." },
    { n: 4, icon: Users, title: "Take the next step", desc: "After mutual interest, eligible members can access contact details per membership rules." },
  ];

  const features = [
    { icon: Compass, title: "Curated Discovery", desc: "A thoughtful daily set of profiles aligned to your preferences." },
    { icon: Search, title: "Browse & Search", desc: "Filter by country, city, age, sect, practice and more." },
    { icon: Sparkles, title: "Compatibility Insights", desc: "Understand shared values before you reach out." },
    { icon: Heart, title: "My Matches", desc: "Track sent, received, mutual and archived interests clearly." },
    { icon: MessageCircle, title: "AI Marriage Coach", desc: "Refine your bio and prepare respectful conversations." },
    { icon: Lock, title: "Profile & Privacy", desc: "Control photos, visibility and who can see your details." },
  ];

  const faqs = [
    { q: "Do I use the same account on web and mobile?", a: "Yes. Your account, profile, photos, interests, matches and Premium are shared across the website, Android and iOS — sign in with the same credentials anywhere." },
    { q: "How does mutual interest work?", a: "When you express interest, the other member is notified. A connection is formed only when both sides express interest. Contact access then follows the current membership and consent rules." },
    { q: "Is my information private?", a: "You control your visibility and photos. Private details are never shown publicly, and contact information is only shared after mutual interest and the required membership conditions are met." },
    { q: "What does Premium include?", a: "Premium unlocks the ability to view contact details of your matches, Wali access, advanced filters and AI compatibility insights. Existing Google Play / Apple subscriptions are recognised automatically on web." },
    { q: "Can I subscribe on the website?", a: "Premium is currently managed through the mobile apps (Google Play / Apple). If you already subscribed there, your Premium is recognised on the website automatically — no need to pay again." },
  ];

  return (
    <div className="min-h-screen bg-ivory text-emerald-950">
      <div className="mx-auto max-w-6xl px-4 pt-4 sm:px-6">
        {/* Header card */}
        <header className="sticky top-4 z-40 rounded-3xl border border-emerald-900/10 bg-white/95 shadow-sm glass">
          <div className="flex h-[64px] items-center justify-between px-4 sm:h-[76px] sm:px-7">
            <a href="/" className="flex items-center gap-3" data-testid="landing-brand">
              <img src="/logo_icon.png" alt="Nikah Match" className="h-11 w-auto shrink-0 object-contain drop-shadow-sm sm:h-12 lg:h-14" />
              <span className="font-serif text-2xl font-bold leading-none tracking-wide whitespace-nowrap sm:text-[28px]">
                <span className="text-emerald-900">Nikah</span> <span className="text-[#B08D2F]">Match</span>
              </span>
            </a>
            <div className="flex items-center gap-3">
              <a href="/marriage-board" data-testid="landing-board-link" className="hidden items-center gap-1.5 rounded-full border border-emerald-800/25 px-4 py-2 text-sm font-semibold text-emerald-900 transition-colors hover:bg-mint sm:inline-flex">Marriage Board</a>
              <a href="/download.html" data-testid="landing-download-btn" className="hidden items-center gap-1.5 rounded-full border border-emerald-800/25 px-4 py-2 text-sm font-semibold text-emerald-900 transition-colors hover:bg-mint sm:inline-flex"><Download className="h-4 w-4" /> Download Mobile App</a>
              <Button variant="outline" onClick={() => navigate("/login")} data-testid="landing-login-btn" className="hidden rounded-full border-emerald-800/40 px-6 py-5 text-emerald-900 hover:bg-mint sm:inline-flex">Log In</Button>
              <Button onClick={() => navigate("/register")} data-testid="landing-register-btn" className="hidden rounded-full bg-emerald-800 px-6 py-5 text-white hover:bg-emerald-900 sm:inline-flex">Get Started <ArrowRight className="ml-2 h-4 w-4" /></Button>
              <Sheet open={menu} onOpenChange={setMenu}>
                <SheetTrigger asChild><button className="rounded-full p-2 text-emerald-900 lg:hidden" data-testid="landing-mobile-menu"><Menu className="h-6 w-6" /></button></SheetTrigger>
                <SheetContent side="right" className="w-72 bg-ivory">
                  <div className="mb-6 mt-1"><Logo size="sm" /></div>
                  <nav className="flex flex-col gap-2">
                    <a href="/download.html" onClick={() => setMenu(false)} data-testid="landing-download-btn-mobile" className="flex items-center gap-2 rounded-xl px-3 py-3 font-medium text-emerald-900 hover:bg-mint"><Download className="h-5 w-5" /> Download Mobile App</a>
                    <Button variant="outline" onClick={() => navigate("/login")} className="rounded-full border-emerald-800/40 text-emerald-900">Log In</Button>
                    <Button onClick={() => navigate("/register")} className="rounded-full bg-emerald-800 text-white">Get Started</Button>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </header>

        {/* Hero panel */}
        <section className="relative mt-4 overflow-hidden rounded-3xl border border-amber-900/10 bg-gradient-to-br from-[#FBF7EF] via-[#FCF6EA] to-[#F4EBD9] shadow-sm">
          {/* Restrained Islamic geometric detail near the edges */}
          <svg aria-hidden="true" className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 text-emerald-800/[0.07]" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1.2">
            <rect x="25" y="25" width="50" height="50" /><rect x="25" y="25" width="50" height="50" transform="rotate(45 50 50)" /><circle cx="50" cy="50" r="36" />
          </svg>
          <svg aria-hidden="true" className="pointer-events-none absolute -bottom-12 -left-12 h-52 w-52 text-[#B08D2F]/[0.08]" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1.2">
            <rect x="25" y="25" width="50" height="50" /><rect x="25" y="25" width="50" height="50" transform="rotate(45 50 50)" /><circle cx="50" cy="50" r="36" />
          </svg>
          <div className="relative grid lg:grid-cols-[1.02fr_0.98fr]">
            <div className="flex flex-col justify-center p-7 sm:p-12 lg:p-14">
              {/* Bismillah badge — matches the badge below */}
              <span className="mb-2.5 inline-flex w-fit items-center gap-2 rounded-full bg-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-800 ring-1 ring-amber-800/15 animate-fade-up" data-testid="hero-basmala">
                <Star className="h-3.5 w-3.5 text-amber-600" /> In The Name Of Allah
              </span>
              <span className="mb-6 inline-flex w-fit items-center gap-2 rounded-full bg-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-800 ring-1 ring-amber-800/15 animate-fade-up hidden sm:inline-flex">
                <Star className="h-3.5 w-3.5 text-amber-600" /> Find Your Partner • Build Your Future
              </span>
              <h1 className="font-serif text-[2.6rem] font-bold leading-[1.08] tracking-tight text-emerald-900 animate-fade-up sm:text-6xl">
                Find your partner. <span className="block text-[#B08D2F]">Build a life rooted in faith.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-emerald-950/70 animate-fade-up hidden sm:block">
                Your journey to nikah starts with a meaningful connection. Meet Muslims who share your values and your intention to marry.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 animate-fade-up">
                <Button size="lg" onClick={() => navigate("/register")} data-testid="hero-cta-btn" className="rounded-full bg-emerald-800 px-7 text-white hover:bg-emerald-900">Create Your Profile <ArrowRight className="ml-2 h-4 w-4" /></Button>
                <p className="text-sm text-emerald-950/70">
                  Already have an account?{" "}
                  <button onClick={() => navigate("/login")} data-testid="hero-login-link" className="font-semibold text-emerald-800 underline decoration-emerald-800/40 underline-offset-4 transition-colors hover:text-emerald-900 hover:decoration-emerald-800">Log in here</button>
                </p>
              </div>
              <nav className="mt-7 animate-fade-up" data-testid="hero-nav-row" aria-label="Explore Nikah Match">
                <div className="grid grid-cols-2 gap-2.5 sm:flex sm:flex-wrap">
                  {NAV.map((n) => (
                    <a key={n.href} href={n.href} data-testid={`hero-nav-${n.href.replace('#', '')}`} className="rounded-full border border-emerald-900/10 bg-white/60 px-4 py-2 text-center text-sm font-medium text-emerald-900/75 transition-colors hover:border-emerald-800/30 hover:bg-white hover:text-emerald-900">{n.label}</a>
                  ))}
                </div>
              </nav>
              <div className="mt-8 flex items-center gap-6 text-sm text-emerald-950/60 hidden sm:flex">
                <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-700" /> Verified members</span>
                <span className="flex items-center gap-1.5"><Lock className="h-4 w-4 text-emerald-700" /> Privacy first</span>
              </div>
            </div>
            <div className="relative flex items-center p-4 sm:p-6 lg:p-8">
              <div className="grid w-full grid-cols-5 gap-3 sm:gap-4">
                <div className="relative col-span-3 overflow-hidden rounded-2xl bg-mint shadow-md ring-1 ring-emerald-900/10">
                  <img
                    src={hero.main.src}
                    alt={hero.main.alt}
                    onError={(e) => onImgError(e, MAIN_FALLBACK)}
                    width="600" height="800" loading="eager"
                    className="aspect-[3/4] w-full object-cover object-top"
                  />
                  {/* Caption integrated into the photo area (replaces floating card) */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-emerald-950/80 via-emerald-950/25 to-transparent p-4 pt-10">
                    <p className="font-serif text-sm font-semibold leading-snug text-white sm:text-base" data-testid="hero-caption">Shared faith. Meaningful connection.<br className="hidden sm:block" /> A future together.</p>
                  </div>
                </div>
                <div className="col-span-2 flex flex-col gap-3 sm:gap-4">
                  <img
                    src={hero.s1.src}
                    alt={hero.s1.alt}
                    onError={(e) => onImgError(e, SUPPORT_FALLBACK)}
                    width="400" height="400" loading="lazy"
                    className="aspect-square w-full rounded-2xl bg-mint object-cover object-center shadow-md ring-1 ring-emerald-900/10"
                  />
                  <img
                    src={hero.s2.src}
                    alt={hero.s2.alt}
                    onError={(e) => onImgError(e, SUPPORT_FALLBACK)}
                    width="400" height="400" loading="lazy"
                    className="aspect-square w-full rounded-2xl bg-mint object-cover object-center shadow-md ring-1 ring-emerald-900/10"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Daily Quranic inspiration */}
      <section className="mx-auto max-w-6xl px-4 pt-8 sm:px-6" data-testid="daily-inspiration">
        <div className="relative overflow-hidden rounded-3xl border border-amber-900/10 bg-gradient-to-br from-emerald-900 to-emerald-950 px-7 py-10 text-center shadow-sm sm:px-12 sm:py-12">
          <svg aria-hidden="true" className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 text-[#D9B968]/10" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="25" y="25" width="50" height="50" /><rect x="25" y="25" width="50" height="50" transform="rotate(45 50 50)" /></svg>
          <p className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#D9B968]"><Star className="h-3.5 w-3.5" /> Daily Inspiration</p>
          <p className="mx-auto max-w-3xl font-serif text-xl leading-relaxed text-white sm:text-2xl" data-testid="daily-verse-text">“{dailyVerse.text}”</p>
          <p className="mt-4 text-sm font-medium text-[#D9B968]" data-testid="daily-verse-ref">— {dailyVerse.ref}</p>
        </div>
      </section>


      {/* A. Principles */}
      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="rounded-3xl border border-emerald-900/10 bg-white p-8 shadow-sm sm:p-12">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-3xl font-bold sm:text-4xl">Built for nikah, not casual dating</h2>
            <p className="mt-3 text-slate-600">Every part of Nikah Match is designed around marriage, compatibility and respect.</p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {principles.map((p) => (
              <div key={p.title} className="rounded-2xl border border-emerald-900/10 bg-ivory p-5">
                <span className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-mint text-emerald-800"><p.icon className="h-5 w-5" /></span>
                <h3 className="font-serif text-lg font-semibold text-emerald-900">{p.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B. How it works */}
      <section id="how" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-8 sm:px-6">
        <div className="rounded-3xl border border-emerald-900/10 bg-mint/40 p-8 shadow-sm sm:p-12">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-3xl font-bold sm:text-4xl">How it works</h2>
            <p className="mt-3 text-slate-600">Four simple steps toward a blessed marriage.</p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-4">
            {steps.map((s) => (
              <div key={s.n} className="relative rounded-2xl border border-emerald-900/10 bg-white p-6 shadow-sm">
                <span className="absolute right-5 top-5 font-serif text-3xl font-bold text-mint">{s.n}</span>
                <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-800 text-white"><s.icon className="h-5 w-5" /></span>
                <h3 className="font-serif text-lg font-semibold text-emerald-900">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* C. Features */}
      <section id="features" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-8 sm:px-6">
        <div className="rounded-3xl border border-emerald-900/10 bg-white p-8 shadow-sm sm:p-12">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-3xl font-bold sm:text-4xl">Everything you need, thoughtfully designed</h2>
            <p className="mt-3 text-slate-600">The same experience you love in the app, adapted beautifully for the web.</p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="group rounded-2xl border border-emerald-900/10 bg-ivory p-6 transition-colors hover:border-emerald-800/30">
                <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-mint text-emerald-800 transition-colors group-hover:bg-emerald-800 group-hover:text-white"><f.icon className="h-6 w-6" /></span>
                <h3 className="font-serif text-xl font-semibold text-emerald-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* D. Compatibility */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="flex justify-center order-2 lg:order-1"><CompatibilityPreview /></div>
          <div className="order-1 lg:order-2">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Compatibility</span>
            <h2 className="mt-2 font-serif text-3xl font-bold sm:text-4xl">See what you share, before you reach out</h2>
            <p className="mt-4 text-slate-600">Nikah Match highlights how your values, practice and preferences align. Scores are a helpful guide to start a conversation with intention — not a guarantee of a successful marriage.</p>
            <ul className="mt-5 space-y-2 text-slate-700">
              {["Deen & practice alignment", "Shared family values", "Lifestyle & goals"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-700" /> {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* E. AI Coach */}
      <section className="border-y border-emerald-900/10 bg-emerald-900 text-white">
        <div className="mx-auto grid max-w-6xl items-center gap-8 px-4 py-16 sm:px-6 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">AI Marriage Coach</span>
            <h2 className="mt-2 font-serif text-3xl font-bold sm:text-4xl">Thoughtful guidance, whenever you need it</h2>
            <p className="mt-4 text-emerald-100/85">Get help improving your bio, suggestions for respectful conversation starters, and guidance on involving your wali. The Coach offers supportive tips — it is not a religious authority or professional counselor.</p>
          </div>
          <div className="rounded-2xl bg-emerald-950/50 p-5 ring-1 ring-white/10">
            <div className="space-y-3">
              <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-amber-500/90 px-4 py-2.5 text-sm text-white">How do I write a strong marriage profile?</div>
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/10 px-4 py-2.5 text-sm text-emerald-50">Lead with your intentions and values. Be specific about the qualities you seek in a spouse, and mention how you'd like your wali involved…</div>
            </div>
          </div>
        </div>
      </section>

      {/* F. Success Stories (live from approved, public stories) */}
      <section id="stories" className="scroll-mt-20 mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <BookHeart className="mx-auto mb-3 h-9 w-9 text-amber-600" />
          <h2 className="font-serif text-3xl font-bold sm:text-4xl">Success Stories</h2>
          <p className="mt-3 text-slate-600">Real, approved stories from couples who found their spouse on Nikah Match.</p>
        </div>

        {stories === null && !storiesError ? (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="stories-loading">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-72 rounded-3xl" />)}
          </div>
        ) : storiesError ? (
          <div className="mx-auto mt-8 max-w-2xl rounded-2xl border border-dashed border-amber-300 bg-white p-8 text-center" data-testid="stories-error">
            <p className="text-slate-600">We couldn't load success stories right now.</p>
            <Button onClick={loadStories} variant="outline" data-testid="stories-retry-btn" className="mt-4 rounded-full border-emerald-800/30 text-emerald-900 hover:bg-mint"><RotateCw className="mr-2 h-4 w-4" /> Try again</Button>
          </div>
        ) : stories.length === 0 ? (
          <div className="mx-auto mt-8 max-w-2xl rounded-2xl border border-dashed border-emerald-300 bg-white p-8 text-center" data-testid="stories-empty">
            <p className="text-slate-500">Approved stories will be featured here soon, inshaAllah. Found your spouse with us?</p>
            <Button onClick={() => navigate("/register")} variant="outline" className="mt-4 rounded-full border-emerald-800/30 text-emerald-900 hover:bg-mint">Share your story</Button>
          </div>
        ) : (
          <div className="mt-10 space-y-6" data-testid="stories-grid">
            {stories[0] && (
              <div className="grid overflow-hidden rounded-3xl border border-emerald-900/10 bg-white shadow-sm animate-fade-up md:grid-cols-2" data-testid="story-featured">
                <div className="relative min-h-56 bg-emerald-900">
                  {stories[0].photo ? (
                    <img src={stories[0].photo} alt={stories[0].names} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full min-h-56 w-full items-center justify-center bg-gradient-to-br from-emerald-800 to-emerald-950"><Heart className="h-16 w-16 fill-current text-rose-400/70" /></div>
                  )}
                  <span className="absolute left-4 top-4 rounded-full bg-rose-500 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">Featured</span>
                </div>
                <div className="flex flex-col justify-center p-7 sm:p-9">
                  <Quote className="h-8 w-8 text-amber-400" />
                  {stories[0].headline && <p className="mt-3 font-serif text-2xl font-bold leading-snug text-emerald-900">{stories[0].headline}</p>}
                  {(stories[0].preview || stories[0].story) && <p className="mt-3 leading-relaxed text-slate-700">{stories[0].preview || stories[0].story}</p>}
                  <div className="mt-5 flex items-center gap-3 border-t border-emerald-900/10 pt-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-mint text-emerald-700"><Heart className="h-4 w-4 fill-current" /></span>
                    <div>
                      <p className="font-serif text-lg font-semibold text-emerald-900">{stories[0].names}</p>
                      {storyMeta(stories[0]) && <p className="flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {storyMeta(stories[0])}</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {stories.length > 1 && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {stories.slice(1).map((s, i) => (
                  <div key={s.id || i} className="flex flex-col overflow-hidden rounded-3xl border border-emerald-900/10 bg-white shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid="story-card">
                    {s.photo && <img src={s.photo} alt={s.names} className="h-44 w-full object-cover" />}
                    <div className="flex flex-1 flex-col p-6">
                      <Quote className="h-7 w-7 text-amber-400" />
                      {s.headline && <p className="mt-2 font-serif text-lg font-semibold text-emerald-900">{s.headline}</p>}
                      {(s.preview || s.story) && <p className="mt-2 leading-relaxed text-slate-700">{s.preview || s.story}</p>}
                      <div className="mt-auto border-t border-emerald-900/10 pt-3">
                        <p className="font-serif text-lg font-semibold text-emerald-900">{s.names}</p>
                        {storyMeta(s) && <p className="flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {storyMeta(s)}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="text-center">
              <Button onClick={() => navigate("/register")} variant="outline" data-testid="stories-share-btn" className="rounded-full border-emerald-800/30 text-emerald-900 hover:bg-mint">Share your story</Button>
            </div>
          </div>
        )}
      </section>

      {/* G. Premium */}
      <section className="border-y border-emerald-900/10 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Membership</span>
            <h2 className="mt-2 font-serif text-3xl font-bold sm:text-4xl">Start free, go further with Premium</h2>
            <p className="mt-3 text-slate-600">Elegant, honest, and the same entitlement across every device.</p>
          </div>
          <div className="mt-8 grid gap-5 lg:grid-cols-3">
            {[
              { name: "Free Membership", price: "$0", per: "No subscription required", features: ["Create and personalize your profile", "Browse and search profiles", "Send, accept, or decline interest", "View mutual matches", "Join Marriage Board discussions", "Read success stories", "Manage your profile visibility"] },
              { name: "Premium Monthly", price: "$4.99", per: "per month", features: ["Unlock contact details with mutual consent", "Private Q&A with your matches", "Ask meaningful questions about marriage and shared goals", "Premium contact requests for parents, walis, imams, and registrars", "Advanced search filters", "AI compatibility insights"] },
              { name: "Premium Annual", price: "$49.99", per: "per year", badge: "Best Value", note: "All Monthly Premium features", features: ["Unlock contact details with mutual consent", "Private Q&A with your matches", "Ask meaningful questions about marriage and shared goals", "Premium contact requests for parents, walis, imams, and registrars", "Advanced search filters", "AI compatibility insights"] },
            ].map((p) => (
              <div key={p.name} data-testid={`plan-${p.name.toLowerCase().replace(/\s+/g, "-")}`} className={`flex flex-col rounded-2xl border-2 p-6 ${p.badge ? "border-amber-500 bg-amber-50/50 shadow-md" : "border-emerald-900/10 bg-ivory"}`}>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-serif text-xl font-semibold text-emerald-900">{p.name}</h3>
                  {p.badge && <span className="shrink-0 rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white">{p.badge}</span>}
                </div>
                <p className="mt-2 font-serif text-3xl font-bold text-emerald-900">{p.price} <span className="text-sm font-normal text-slate-500">{p.per}</span></p>
                {p.note && <p className="mt-3 text-sm font-semibold text-amber-700">{p.note}</p>}
                <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
                  {p.features.map((t) => <li key={t} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" /> {t}</li>)}
                </ul>
                <Button onClick={() => navigate("/register")} data-testid={`plan-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`} className={`mt-6 w-full rounded-full py-5 ${p.badge ? "bg-amber-500 text-white hover:bg-amber-600" : p.price === "$0" ? "bg-emerald-800 text-white hover:bg-emerald-900" : "bg-emerald-800 text-white hover:bg-emerald-900"}`}>
                  {p.price === "$0" ? "Create free account" : "Get Premium"}
                </Button>
              </div>
            ))}
          </div>
          <p className="mt-5 text-center text-xs text-slate-500">Premium is currently managed through the mobile apps. Existing Google Play / Apple subscriptions are recognised on web automatically.</p>
        </div>
      </section>

      {/* H. FAQ */}
      <section id="faq" className="scroll-mt-20 mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <h2 className="text-center font-serif text-3xl font-bold sm:text-4xl">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="mt-8">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-emerald-900/10">
              <AccordionTrigger className="text-left font-medium text-emerald-900 hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* I. Final CTA */}
      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
        <div className="rounded-[2rem] bg-emerald-900 px-8 py-14 text-center text-white sm:px-16">
          <h2 className="font-serif text-3xl font-bold sm:text-4xl">Begin your journey toward nikah.</h2>
          <p className="mx-auto mt-3 max-w-xl text-emerald-100/85">Create your profile today and meet Muslims who share your intention for marriage.</p>
          <Button size="lg" onClick={() => navigate("/register")} data-testid="footer-cta-btn" className="mt-8 rounded-full bg-amber-500 px-8 text-white hover:bg-amber-600">Create Your Profile</Button>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
